import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Detects redirects from the Azure AD admin consent endpoint and surfaces
 * success/failure feedback to the user. The handler is mounted at the root
 * layout so it runs regardless of which route is active.
 */
export function AdminConsentHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastHandledSignal = useRef<string | null>(null);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const state = search.get("state");
    const adminConsentFlag = search.get("admin_consent");
    const error = search.get("error");
    const errorDescription = search.get("error_description");

    const isAdminConsentState = typeof state === "string" && state.startsWith("admin_consent_");
    const hasAdminConsentSignal = !!(adminConsentFlag || isAdminConsentState || error);

    if (!hasAdminConsentSignal) {
      return;
    }

    const signalKey = `${adminConsentFlag ?? ""}|${state ?? ""}|${error ?? ""}|${errorDescription ?? ""}`;
    if (lastHandledSignal.current === signalKey) {
      return;
    }
    lastHandledSignal.current = signalKey;

    if (error) {
      toast.error("Admin consent failed", {
        description: errorDescription ? decodeURIComponent(errorDescription) : error,
        duration: 10000,
      });
    } else if (adminConsentFlag?.toLowerCase() === "true") {
      toast.success("Admin consent granted", {
        description: "Tenant-wide access approved. You can now sign in normally.",
        duration: 8000,
      });
    } else if (isAdminConsentState) {
      toast.info("Admin consent cancelled", {
        description: "No changes were made. Try again with a Global Administrator account.",
        duration: 6000,
      });
    }

    navigate("/admin-consent", { replace: true });
  }, [location, navigate]);

  return null;
}

export default AdminConsentHandler;
